/**
 * Event Block
 *
 * Renders a single task or calendar event in the time grid.
 * Positioned absolutely within a TimeColumn.
 */

import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import clsx from 'clsx';
import { ResizeHandle } from './ResizeHandle';
import type { PositionedItem } from '@/hooks/useScheduler';

interface EventBlockProps {
  item: PositionedItem;
  isDragging?: boolean;
  isGhost?: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
  onResizePointerDown?: (e: React.PointerEvent, edge: 'top' | 'bottom') => void;
  onClick?: () => void;
}

const priorityColors: Record<string, string> = {
  '#f43f5e': 'border-l-accent-rose',    // urgent
  '#f59e0b': 'border-l-accent-amber',   // high
  '#3b82f6': 'border-l-accent-blue',    // normal
  '#10b981': 'border-l-accent-emerald', // low
};

export function EventBlock({
  item,
  isDragging = false,
  isGhost = false,
  onPointerDown,
  onResizePointerDown,
  onClick,
}: EventBlockProps) {
  const startDate = new Date(item.startTime);
  const endDate = new Date(item.endTime);
  const timeLabel = `${format(startDate, 'HH:mm', { locale: it })} - ${format(endDate, 'HH:mm', { locale: it })}`;
  const durationMin = Math.round((item.endTime - item.startTime) / 60000);
  const isShort = item.height < 40;
  const isGoogleEvent = item.type === 'calendar_event';

  // Calculate position within column
  const columnWidth = `calc((100% - 2px) / ${item.totalColumns})`;
  const columnLeft = `calc(${item.column} * (100% / ${item.totalColumns}) + 1px)`;

  return (
    <div
      className={clsx(
        'absolute rounded-md border-l-[3px] overflow-hidden transition-shadow',
        'select-none',
        isGhost && 'opacity-40 pointer-events-none border-dashed',
        isDragging && 'opacity-70 shadow-lg z-30',
        !isDragging && !isGhost && 'hover:shadow-md cursor-grab active:cursor-grabbing',
        isGoogleEvent
          ? 'bg-accent-violet/10 border-l-accent-violet/60'
          : item.color && priorityColors[item.color]
            ? priorityColors[item.color]
            : 'border-l-border-secondary',
        !isGoogleEvent && 'bg-surface-primary',
      )}
      style={{
        top: `${item.top}px`,
        height: `${Math.max(item.height, 20)}px`,
        left: columnLeft,
        width: columnWidth,
      }}
      onPointerDown={item.isReadOnly ? undefined : onPointerDown}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {/* Resize handles (not for read-only / google events) */}
      {!item.isReadOnly && !isGhost && (
        <>
          <ResizeHandle
            edge="top"
            onPointerDown={(e) => onResizePointerDown?.(e, 'top')}
          />
          <ResizeHandle
            edge="bottom"
            onPointerDown={(e) => onResizePointerDown?.(e, 'bottom')}
          />
        </>
      )}

      {/* Content */}
      <div className={clsx('px-1.5 py-0.5 h-full flex flex-col', isShort && 'flex-row items-center gap-1')}>
        {isGoogleEvent && (
          <CalendarIcon className="w-2.5 h-2.5 text-accent-violet/60 shrink-0" />
        )}
        <span
          className={clsx(
            'font-medium truncate',
            isShort ? 'text-[9px]' : 'text-[10px]',
            isGoogleEvent ? 'text-accent-violet/80' : 'text-text-primary'
          )}
        >
          {item.title}
        </span>
        {!isShort && (
          <span className="text-[9px] text-text-quaternary truncate">
            {timeLabel}
            {durationMin > 0 && ` · ${durationMin}min`}
          </span>
        )}
      </div>
    </div>
  );
}
